import { usePatientSubscription } from '@ctw/shared/api/subscriptions/subscriptions';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { RequestRecordsButton } from '@ctw/shared/content/patient-history/request-records-button';
import { usePatientHistory } from '@ctw/shared/content/patient-history/use-patient-history';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import {
  type IconDefinition,
  faArrowsRotate,
  faCircleExclamation,
  faWarning,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';

interface PatientHistoryStatus {
  icon: IconDefinition;
  label: string;
  message?: string;
}

interface PatientHistoryStatusProps {
  expanded: boolean;
  inline?: boolean;
}

export const PatientHistoryStatus = ({ expanded, inline }: PatientHistoryStatusProps) => {
  const builderConfig = useBuilderConfig();
  const {
    details: { completed, inProgress, nextScheduledAt },
    ...patientHistory
  } = usePatientHistory();
  const patientSubscription = usePatientSubscription();

  const isLoading = useMemo(
    () => patientHistory.isLoading || patientSubscription.isLoading,
    [patientHistory.isLoading, patientSubscription.isLoading],
  );

  const isError = useMemo(
    () => patientHistory.isError || patientSubscription.isError,
    [patientHistory.isError, patientSubscription.isError],
  );

  const status: PatientHistoryStatus = useMemo(
    () =>
      (() => {
        if (isError) {
          return {
            icon: faCircleExclamation,
            label: 'Unable to load records request information',
          };
        }

        if (inProgress) {
          if (inProgress?.derivedStatus === 'partiallyComplete') {
            return {
              icon: faArrowsRotate,
              label: 'Current request partially complete',
              message: 'Patient records are being retrieved',
            };
          }

          return {
            icon: faArrowsRotate,
            label: 'Request in-progress',
            message: 'Patient records are being retrieved',
          };
        }

        if (completed) {
          if (completed.derivedStatus === 'failed') {
            return {
              icon: faCircleExclamation,
              label: `Last request failed ${completed.targetDate}`,
              message: 'There was an error fetching records for this patient',
            };
          }

          if (completed.derivedStatus === 'completeWithErrors') {
            return {
              icon: faWarning,
              label: `Last updated ${completed.targetDate}`,
              message: 'There was an error fetching some records for this patient',
            };
          }

          return {
            icon: faArrowsRotate,
            label: `Last updated ${completed.targetDate}`,
          };
        }

        if (nextScheduledAt) {
          return {
            icon: faArrowsRotate,
            label: `Next scheduled request ${nextScheduledAt}`,
          };
        }

        return {
          icon: faArrowsRotate,
          label: 'Never requested',
        };
      })(),
    [isError, completed, inProgress, nextScheduledAt],
  );

  if (isLoading) {
    if (inline) {
      return <LoadingSpinner centered={true} className={tw`text-xs`} iconClass={tw`h-3 w-3`} />;
    }

    return <LoadingSpinner message="Loading..." className={tw`w-full whitespace-nowrap`} />;
  }

  const iconWithTooltip = (
    <Tooltip
      id="patient-history-status"
      content={status.label}
      className={tw`flex items-center justify-center`}
      hidden={expanded}
    >
      <FontAwesomeIcon
        icon={status.icon}
        className={tw`h-[14px] min-h-[14px] w-[14px] min-w-[14px] pl-0.5 text-content-icon transition-all duration-300 group-hover:text-content-main`}
      />
    </Tooltip>
  );

  return (
    <div
      className={twx('flex flex-col gap-1 overflow-hidden', {
        'h-full min-h-full flex-row items-center': inline,
      })}
    >
      <div className={tw`flex items-center gap-1`}>
        {builderConfig.showRequestRecords ? (
          <RequestRecordsButton className="group overflow-hidden whitespace-nowrap text-sm transition-all duration-500">
            {iconWithTooltip}
          </RequestRecordsButton>
        ) : (
          iconWithTooltip
        )}

        <div className={tw`flex flex-col items-start justify-start gap-1 self-stretch`}>
          <div
            className={twx(
              'ml-1 overflow-hidden whitespace-nowrap font-normal text-content-subtle text-sm leading-tight transition-all duration-500',
              {
                'opacity-0': !expanded,
                'opacity-100': expanded,
                'w-0': !expanded && inline,
              },
            )}
          >
            {status.label}
          </div>
        </div>
      </div>
    </div>
  );
};
